import { diff as jsonDiff, type Delta } from 'jsondiffpatch';
import { Dietary, DietaryRestrictions } from '@hungryroot/constants';
import { log, logUnknownErrorResponse } from '@hungryroot/o11y';
import {
  type LikeDislikeTagsRatings,
  type PatchedCustomerOccasionEntity,
  type ContainerFrequencyOptions,
  patchApiV2CustomersCustomerIdDietaryRestrictions,
  patchApiV2CustomersCustomerIdDishTypes,
  type PatchedCustomerDishType as DishTypeResponse,
  patchApiV2CustomersOccasionEntityPreferences,
  putApiV2CustomersCustomerIdOccasionGoals,
  patchApiV2CustomersCustomerIdQuizzes,
  postApiV2CustomersCustomerIdQuizzes,
  patchApiV2CustomersCustomerIdTags,
  patchCustomerPeriodicities,
  PeriodicityTypes,
  StatusCode,
} from '@hungryroot/rest';
import { type HouseholdSize, EatingHabit, type Quiz } from '../defs/quiz';
import { toPascalCase } from '../utils/toPascalCase';

function getGoalsPayload(quiz: Quiz) {
  return {
    occasion_goals: quiz.goals ? quiz.goals.map((id) => ({ id })) : [],
  };
}

interface CustomersQuizzes {
  customer_id: number;
  household_size: HouseholdSize | 0;
  num_adults: number;
  num_young_children: number;
  num_older_children: number;
  weekly_grocery_spend: number | null;
  breakfast_meal_snack: null;
  lunch_meal_snack: null;
  dinner_meal_snack: null;
  breakfast_frequency: ContainerFrequencyOptions | null;
  lunch_frequency: ContainerFrequencyOptions | null;
  dinner_frequency: ContainerFrequencyOptions | null;
  snack_frequency: ContainerFrequencyOptions | null;
  sweets_frequency: ContainerFrequencyOptions | null;
  fruit_frequency: null;
}

export interface QuizOptions {
  originalQuiz?: Quiz;
  // Mini-quiz creates the quizzes row on signup without householdSize.
  // Without this, a null householdSize would POST on every Continue (409).
  existingQuiz?: boolean;
}

type OccasionGoalsData = {
  occasion_goals: { id: number; name: string }[];
};

export function getNumAdults(quiz: Quiz) {
  // `quiz.adultsCount` should be present as of 08/2024, but below we'll handle cases where legacy customers
  // are trying to finish signup
  if (quiz.adultsCount) {
    return quiz.adultsCount;
  }

  if (!quiz.householdSize) {
    return 0;
  } else if (quiz.householdSize <= 2) {
    return quiz.householdSize;
  }
  return 1;
}

export function getTotalPeople(quiz: Quiz) {
  const { adultsCount, childrenCount } = quiz;

  return (adultsCount ?? 0) + (childrenCount ?? 0);
}

/**
 * The BE only allows values 1, 2, or 3 for household size. If the total people is greater than 3, we'll cap it at 3.
 */
export function deriveHouseholdSizeFromTotalPeople(
  totalPeople: number
): HouseholdSize {
  if (totalPeople > 3) {
    return 3;
  } else if ([1, 2, 3].includes(totalPeople)) {
    return totalPeople;
  }

  return 1;
}

export function getCustomersQuizzesPayload(
  quiz: Quiz,
  customerId: number
): CustomersQuizzes {
  // If this value is available, we will use it, but in some scenarios it's inferred based on total people
  let householdSize = quiz.householdSize;

  // In Exp. 160, we populate quiz.householdSize so that we never need to infer it.
  //
  // In some historic scenarios, we infer it once when the user signs up, but then it can get out of sync,
  // if they go back to the quiz and change the number of people in their household. We'll keep that behavior for now.
  if (householdSize === null) {
    // Infer household size based on total adults + children (ages 9 and over)
    const totalPeople = getTotalPeople(quiz);

    householdSize = deriveHouseholdSizeFromTotalPeople(totalPeople);
  }

  const num_adults = getNumAdults(quiz);

  return {
    customer_id: customerId,
    household_size: householdSize,
    num_adults,
    num_young_children: quiz.youngChildrenCount ?? 0,
    num_older_children: quiz.childrenCount ?? 0,
    weekly_grocery_spend: null,
    breakfast_meal_snack: null,
    lunch_meal_snack: null,
    dinner_meal_snack: null,
    breakfast_frequency: quiz.breakfastFrequency,
    lunch_frequency: quiz.lunchFrequency,
    dinner_frequency: quiz.dinnerFrequency,
    snack_frequency: quiz.snacksFrequency,
    sweets_frequency: quiz.sweetsFrequency,
    fruit_frequency: null,
  };
}

export function getDietaryPreferencesPayload(
  customerId: number,
  quiz: Quiz
): {
  id: DietaryRestrictions;
  selected: boolean;
}[] {
  const dietaryPreferences: {
    id: DietaryRestrictions;
    selected: boolean;
  }[] = [];

  const habits = Object.keys(EatingHabit).map((habit) => {
    const key = toPascalCase(habit);
    return {
      id: DietaryRestrictions[key as keyof typeof DietaryRestrictions],
      selected: false,
    };
  });

  if (quiz.habits) {
    const key = toPascalCase(quiz.habits);
    const filteredHabits = habits.filter(
      (habit) =>
        habit.id !==
        DietaryRestrictions[key as keyof typeof DietaryRestrictions]
    );
    dietaryPreferences.push(...filteredHabits, {
      id: DietaryRestrictions[key as keyof typeof DietaryRestrictions],
      selected: true,
    });
  } else {
    dietaryPreferences.push(...habits);
  }

  const dietary = Object.keys(Dietary).map((habit) => {
    const key = toPascalCase(habit);
    return {
      id: DietaryRestrictions[key as keyof typeof DietaryRestrictions],
      selected: false,
    };
  });

  if (quiz.dietary) {
    const quizDietary = quiz.dietary.map((dietaryPref) => {
      const key = toPascalCase(dietaryPref);
      return {
        id: DietaryRestrictions[key as keyof typeof DietaryRestrictions],
        selected: true,
      };
    });
    const filteredDietary = dietary.filter((obj1) => {
      return !quizDietary.some((obj2) => {
        return obj1.id === obj2.id;
      });
    });
    dietaryPreferences.push(...filteredDietary, ...quizDietary);
  }
  log('quiz_dietary_restrictions_format', {
    customerId,
    description:
      'formatting the data to be used in dietary restrictions patch, as well as the quiz',
    dietaryPreferences,
    quiz,
  });
  return dietaryPreferences;
}

export function getDishTypesPayload(
  quiz: Quiz,
  customerId: number
): DishTypeResponse[] {
  const dishTypes: DishTypeResponse[] = [];
  quiz.lunchDinner?.map((dishType) => {
    dishTypes.push({ customer_id: customerId, dish_type: dishType, rating: 1 });
  });
  return dishTypes;
}

interface TagResponse {
  tag: number;
  preference: LikeDislikeTagsRatings;
  is_explicit: boolean;
}

interface TagResponseWithCustomerId extends TagResponse {
  customer_id: number;
}

export function getTagsPayload(
  quiz: Quiz,
  customerId: number
): TagResponseWithCustomerId[] {
  const tags: TagResponseWithCustomerId[] = [];

  quiz.cuisines?.map((tag) => {
    tags.push({
      customer_id: customerId,
      tag: tag.id,
      is_explicit: true,
      preference: tag.liked,
    });
  });
  quiz.flavors?.map((tag) => {
    tags.push({
      customer_id: customerId,
      tag: tag.id,
      is_explicit: true,
      preference: tag.liked,
    });
  });
  quiz.meats?.map((tag) => {
    tags.push({
      customer_id: customerId,
      tag: tag.id,
      is_explicit: true,
      preference: tag.rated,
    });
  });
  quiz.seafood?.map((tag) => {
    tags.push({
      customer_id: customerId,
      tag: tag.id,
      is_explicit: true,
      preference: tag.rated,
    });
  });
  quiz.plants?.map((tag) => {
    tags.push({
      customer_id: customerId,
      tag: tag.id,
      is_explicit: true,
      preference: tag.rated,
    });
  });
  quiz.dislike?.map((id) => {
    tags.push({
      customer_id: customerId,
      tag: id,
      is_explicit: true,
      preference: -1,
    });
  });

  quiz.breakfast?.map((id) => {
    tags.push({
      customer_id: customerId,
      tag: id,
      is_explicit: true,
      preference: 1,
    });
  });

  quiz.lunch?.map((id) => {
    tags.push({
      customer_id: customerId,
      tag: id,
      is_explicit: true,
      preference: 1,
    });
  });

  quiz.dinner?.map((id) => {
    tags.push({
      customer_id: customerId,
      tag: id,
      is_explicit: true,
      preference: 1,
    });
  });

  quiz.snacks?.map((id) => {
    tags.push({
      customer_id: customerId,
      tag: id,
      is_explicit: true,
      preference: 1,
    });
  });

  quiz.sweets?.map((id) => {
    tags.push({
      customer_id: customerId,
      tag: id,
      is_explicit: true,
      preference: 1,
    });
  });

  quiz.macroNutrient?.map((id) => {
    tags.push({
      customer_id: customerId,
      tag: id,
      is_explicit: true,
      preference: 1,
    });
  });

  quiz.healthGoals?.map((id) => {
    tags.push({
      customer_id: customerId,
      tag: id,
      is_explicit: true,
      preference: 1,
    });
  });

  quiz.foodQualities
    ?.filter((id) => id !== null)
    .map((id) => {
      tags.push({
        customer_id: customerId,
        tag: id,
        is_explicit: true,
        preference: 1,
      });
    });

  quiz.foodFormats
    ?.filter((id) => id !== null)
    .map((id) => {
      tags.push({
        customer_id: customerId,
        tag: id,
        is_explicit: true,
        preference: 1,
      });
    });

  quiz.readyMade?.map((id) => {
    tags.push({
      customer_id: customerId,
      tag: id,
      is_explicit: true,
      preference: 1,
    });
  });

  if (quiz.budget !== undefined && quiz.budget !== null) {
    tags.push({
      customer_id: customerId,
      tag: quiz.budget,
      is_explicit: true,
      preference: 1,
    });
  }

  return tags;
}

function indexTagResponses(
  tags: TagResponseWithCustomerId[]
): Record<string, TagResponseWithCustomerId> {
  return tags.reduce(
    (seed, tag) => {
      seed[tag.tag] = tag;
      return seed;
    },
    {} as Record<string, TagResponseWithCustomerId>
  );
}

export function renderTagsPayloadDiff(
  original: Record<string, TagResponseWithCustomerId>,
  diff: Delta
): TagResponseWithCustomerId[] {
  const responses: TagResponseWithCustomerId[] = [];

  Object.keys(diff).forEach((key) => {
    const value = diff[key] as
      | {
          preference?: [
            TagResponseWithCustomerId['preference'],
            TagResponseWithCustomerId['preference'],
          ];
        }
      | [TagResponseWithCustomerId]
      | [TagResponseWithCustomerId, 0, 0];

    // Item inserted or removed
    if (Array.isArray(value)) {
      // Item removed
      if (value[1] === 0 && value[2] === 0) {
        const originalValue = original[key];
        const { customer_id, tag, is_explicit } = originalValue;
        responses.push({
          customer_id,
          tag,
          is_explicit,
          preference: 0,
        });
        return;
      }

      responses.push(value[0]);
      return;
    }

    const originalValue = original[key];
    const { customer_id, tag, is_explicit } = originalValue;

    const { preference } = value;
    if (preference !== undefined) {
      // Tag preferenced changed, record id
      responses.push({
        customer_id,
        tag,
        is_explicit,
        preference: preference[1],
      });
      return;
    }
  });

  return responses;
}

export function diffDishtypePreferencesPayload(
  customerId: number,
  diff: Delta
): DishTypeResponse[] {
  const responses: DishTypeResponse[] = [];

  Object.keys(diff)
    .filter((item) => item !== '_t')
    .forEach((key) => {
      const value = diff[key];
      // Item moved
      if (key.startsWith('_')) {
        const [dishTypeId, from, to] = value;

        // Item removed
        if (from === 0 && to === 0) {
          responses.push({
            customer_id: customerId,
            dish_type: dishTypeId,
            rating: 0,
          });
          return;
        }
      }

      // Item added or modified
      const [originalValue, modifiedValue] = value;
      if (modifiedValue === undefined) {
        // Item was added
        responses.push({
          customer_id: customerId,
          dish_type: originalValue,
          rating: 1,
        });
        return;
      }
    });

  return responses;
}

async function postCustomerQuizzesWithConflictFallback(
  quizPayload: CustomersQuizzes
) {
  const created = await postApiV2CustomersCustomerIdQuizzes({
    quiz: quizPayload,
  });
  if (created.status === StatusCode.ClientErrorConflict) {
    return patchApiV2CustomersCustomerIdQuizzes({
      quiz: quizPayload,
    });
  }
  return created;
}

async function patchCustomerQuizzesWithMissingFallback(
  quizPayload: CustomersQuizzes
) {
  const updated = await patchApiV2CustomersCustomerIdQuizzes({
    quiz: quizPayload,
  });
  if (updated.status === StatusCode.ClientErrorNotFound) {
    // The create can still land between our 404 and this POST (mini-quiz
    // signup fires `invalidateCustomerData` before its own create resolves),
    // so go back through the conflict-tolerant create rather than a bare
    // POST. That makes the pair idempotent under either interleaving.
    return postCustomerQuizzesWithConflictFallback(quizPayload);
  }
  return updated;
}

function persistCustomerQuizzes(
  customerId: number,
  quiz: Quiz,
  options?: QuizOptions
): Promise<unknown> | null {
  const originalQuiz = options?.originalQuiz;
  const shouldCreateQuiz =
    originalQuiz === undefined ||
    ((originalQuiz.householdSize === null ||
      originalQuiz.householdSize === undefined) &&
      options?.existingQuiz !== true);

  if (shouldCreateQuiz || originalQuiz === undefined) {
    return postCustomerQuizzesWithConflictFallback(
      getCustomersQuizzesPayload(quiz, customerId)
    );
  }

  const quizzesPayload = getCustomersQuizzesPayload(quiz, customerId);

  if (
    jsonDiff(
      quizzesPayload,
      getCustomersQuizzesPayload(originalQuiz, customerId)
    )
  ) {
    return patchCustomerQuizzesWithMissingFallback(quizzesPayload);
  }

  return null;
}

function persistGoals(
  customerId: number,
  quiz: Quiz,
  originalQuiz?: Quiz
): Promise<unknown> | null {
  const goalsPayload = getGoalsPayload(quiz);

  if (originalQuiz === undefined) {
    return putApiV2CustomersCustomerIdOccasionGoals({
      requestBody: goalsPayload,
    });
  }

  if (jsonDiff(goalsPayload, getGoalsPayload(originalQuiz)) !== undefined) {
    return putApiV2CustomersCustomerIdOccasionGoals({
      requestBody: goalsPayload,
    });
  }

  return null;
}

function persistDietaryPreferences(
  customerId: number,
  quiz: Quiz,
  originalQuiz?: Quiz
): Promise<unknown> | null {
  const dietaryPreferencesPayload = getDietaryPreferencesPayload(
    customerId,
    quiz
  );

  log('quiz_dietary_restrictions_patch', {
    customerId,
    description: 'body request data used for dietary restrictions patch',
    dietaryPreferencesPayload,
  });

  if (originalQuiz === undefined) {
    return patchApiV2CustomersCustomerIdDietaryRestrictions({
      requestBody: {
        dietary_restrictions: dietaryPreferencesPayload,
      },
    });
  }

  if (
    jsonDiff(
      dietaryPreferencesPayload,
      getDietaryPreferencesPayload(customerId, originalQuiz)
    ) !== undefined
  ) {
    return patchApiV2CustomersCustomerIdDietaryRestrictions({
      requestBody: {
        dietary_restrictions: dietaryPreferencesPayload,
      },
    });
  }
  return null;
}

// nothing should populate lunchDinner anymore so we don't send any of our lunchDinner tags to
// and we should be able to remove this
function persistDishTypes(
  customerId: number,
  quiz: Quiz,
  originalQuiz?: Quiz
): Promise<unknown> | null {
  const filteredDishTypeQuiz =
    quiz.lunchDinner !== null && quiz.lunchDinner.length > 0
      ? quiz.lunchDinner.filter((dishtype) => dishtype !== 18)
      : [];
  const filteredQuiz = { ...quiz, lunchDinner: filteredDishTypeQuiz };

  if (originalQuiz === undefined) {
    return patchApiV2CustomersCustomerIdDishTypes({
      requestBody: getDishTypesPayload(filteredQuiz, customerId),
    });
  }

  const filteredDishTypeOriginalQuiz =
    originalQuiz &&
    originalQuiz.lunchDinner !== null &&
    originalQuiz.lunchDinner.length > 0
      ? originalQuiz.lunchDinner.filter((dishtype) => dishtype !== 18)
      : [];
  const filteredOriginalQuiz = {
    ...originalQuiz,
    lunchDinner: filteredDishTypeOriginalQuiz,
  };
  const dishTypesDiff = jsonDiff(
    filteredOriginalQuiz.lunchDinner,
    filteredQuiz.lunchDinner
  );
  if (dishTypesDiff !== undefined) {
    return patchApiV2CustomersCustomerIdDishTypes({
      requestBody: diffDishtypePreferencesPayload(customerId, dishTypesDiff),
    });
  }

  if (originalQuiz === undefined) {
    patchApiV2CustomersCustomerIdDishTypes({
      requestBody: getDishTypesPayload(quiz, customerId),
    });
  }

  return null;
}

function persistTags(
  customerId: number,
  quiz: Quiz,
  originalQuiz?: Quiz
): Promise<unknown> | null {
  if (originalQuiz === undefined) {
    return patchApiV2CustomersCustomerIdTags({
      requestBody: getTagsPayload(quiz, customerId),
    });
  }

  const originalIndexedTags = indexTagResponses(
    getTagsPayload(originalQuiz, customerId)
  );

  const tagsDiff = jsonDiff(
    originalIndexedTags,
    indexTagResponses(getTagsPayload(quiz, customerId))
  );

  if (tagsDiff !== undefined) {
    const diffedPayload = renderTagsPayloadDiff(originalIndexedTags, tagsDiff);
    return patchApiV2CustomersCustomerIdTags({
      requestBody: diffedPayload,
    });
  }
  return null;
}

function getArrayDifferences(arr1: number[], arr2: number[]) {
  const set2 = new Set(arr2);
  const set1 = new Set(arr1);

  return {
    deleted: arr1.filter((item) => !set2.has(item)),
    added: arr2.filter((item) => !set1.has(item)),
  };
}

const SWIPE_RATING_PREFERENCE = {
  positive: 1,
  negative: -1,
  skip: 0,
} as const;

function getSwipeRatingsPayload(
  quiz: Quiz
): PatchedCustomerOccasionEntity[] | undefined {
  const payload = (quiz.swipeRatings ?? []).map((rating) => ({
    object_id: rating.itemId,
    content_type: rating.itemType,
    rating_context: 'funnel',
    preference: SWIPE_RATING_PREFERENCE[rating.rating],
  }));

  return payload.length > 0 ? payload : undefined;
}

function patchOccasionEntityPreferences(
  payload: PatchedCustomerOccasionEntity[] | undefined
): Promise<unknown> | null {
  if (payload === undefined) {
    return null;
  }

  return patchApiV2CustomersOccasionEntityPreferences(payload);
}

function persistDislikedProducts(
  customerId: number,
  quiz: Quiz,
  originalQuiz?: Quiz
): Promise<unknown> | null {
  const dislikedProductIds = quiz.dislikedProductIds;
  if (!dislikedProductIds || dislikedProductIds.length === 0) {
    return null;
  }

  const { added } = getArrayDifferences(
    originalQuiz?.dislikedProductIds ?? [],
    dislikedProductIds
  );

  if (added.length === 0) {
    return null;
  }

  return patchCustomerPeriodicities({
    customerId,
    data: added.map((product) => ({
      product,
      periodicity: PeriodicityTypes.NEVER,
    })),
  });
}

function persistLikedProducts(
  customerId: number,
  quiz: Quiz,
  originalQuiz?: Quiz
): Promise<unknown> | null {
  const likedProductIds = quiz.likedProductIds;
  if (!likedProductIds || likedProductIds.length === 0) {
    return null;
  }

  const { added } = getArrayDifferences(
    originalQuiz?.likedProductIds ?? [],
    likedProductIds
  );

  if (added.length === 0) {
    return null;
  }

  return patchCustomerPeriodicities({
    customerId,
    data: added.map((product) => ({
      product,
      periodicity: PeriodicityTypes.OFTEN,
    })),
  });
}

export async function serverPersist(
  customerId: number,
  quiz: Quiz,
  options?: QuizOptions
): Promise<void> {
  const promises: Promise<unknown>[] = [];

  const patchOccasionEntityPreferencesPromise = patchOccasionEntityPreferences(
    getSwipeRatingsPayload(quiz)
  );

  if (patchOccasionEntityPreferencesPromise !== null) {
    promises.push(patchOccasionEntityPreferencesPromise);
  }

  const persistCustomerQuizzesPromise = persistCustomerQuizzes(
    customerId,
    quiz,
    options
  );

  if (persistCustomerQuizzesPromise !== null) {
    promises.push(persistCustomerQuizzesPromise);
  }

  const persistGoalsPromise = persistGoals(
    customerId,
    quiz,
    options?.originalQuiz
  );

  if (persistGoalsPromise !== null) {
    promises.push(persistGoalsPromise);
  }

  const dietaryPreferencesPromise = persistDietaryPreferences(
    customerId,
    quiz,
    options?.originalQuiz
  );

  if (dietaryPreferencesPromise !== null) {
    promises.push(dietaryPreferencesPromise);
  }

  const dishTypePromise = persistDishTypes(
    customerId,
    quiz,
    options?.originalQuiz
  );

  if (dishTypePromise !== null) {
    promises.push(dishTypePromise);
  }

  const tagsPromise = persistTags(customerId, quiz, options?.originalQuiz);

  if (tagsPromise !== null) {
    promises.push(tagsPromise);
  }

  const dislikedProductsPromise = persistDislikedProducts(
    customerId,
    quiz,
    options?.originalQuiz
  );

  if (dislikedProductsPromise !== null) {
    promises.push(dislikedProductsPromise);
  }

  const likedProductsPromise = persistLikedProducts(
    customerId,
    quiz,
    options?.originalQuiz
  );

  if (likedProductsPromise !== null) {
    promises.push(likedProductsPromise);
  }

  const result = await Promise.all(promises);

  // The occasion_goals PUT request returns a 200 response even when a given goal is set to inactive in admin. The diff below evaluates that response data to determine if selected goals have all been persisted.
  const occasionGoalsResult = result.find(
    (r): r is { data: OccasionGoalsData } =>
      Boolean(
        r !== null &&
          typeof r === 'object' &&
          'data' in r &&
          r.data?.hasOwnProperty('occasion_goals')
      )
  );
  const persistedGoalsById = occasionGoalsResult?.data?.occasion_goals.map(
    (goal) => goal.id
  );
  const quizGoalsById = getGoalsPayload(quiz).occasion_goals.map(
    (goal) => goal.id
  );

  const inactiveGoals = persistedGoalsById
    ? quizGoalsById.filter((id) => !persistedGoalsById.includes(id))
    : [];

  if (inactiveGoals.length > 0) {
    logUnknownErrorResponse('Failed to persist selected goals in quiz', {
      key: 'quiz.serverPersist.goals',
      customerId,
      inactiveGoals,
      quizGoalsById,
      persistedGoalsById,
    });
  }
}
